import { useEffect, useState, useCallback, useMemo } from "react";
import { useColumnResize } from "@/hooks/useColumnResize";
import { useNavigate } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PlusIcon as Plus, WrenchIcon as Wrench, ClockIcon as Clock, MagnifyingGlassIcon as MagnifyingGlass, WarningCircleIcon as WarningCircle, PackageIcon as Package, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown } from "@phosphor-icons/react";
import { RIP_STATI, RIP_STATO_COLOR } from "@/lib/riparazioni";

function StatCard({ label, value, tone = "primary" }) {
  const tones = {
    primary: "text-primary", amber: "text-amber-600", teal: "text-teal-600",
    orange: "text-orange-600", rose: "text-rose-600", emerald: "text-emerald-600", violet: "text-violet-600",
    cyan: "text-cyan-600",
  };
  return (
    <Card className="rounded-sm border-border shadow-none p-4" data-testid={`rip-stat-${label}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold leading-tight">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 tabular-nums ${tones[tone]}`}>{value}</div>
    </Card>
  );
}

export default function Repairs() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const DW_REP = { numero: 120, cliente: 180, dispositivo: 130, marca: 180, seriale: 150, stato: 130, tecnico: 150, ritiro: 120, giorni: 120 };
  const { colW, startResize, totalW } = useColumnResize("repairs_col_w", DW_REP);
  const RH = ({ k }) => <span onMouseDown={(e) => startResize(e, k)} onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60" />;
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [fStato, setFStato] = useState("all");
  const [dataDa, setDataDa] = useState("");
  const [dataA, setDataA] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const sortIcon = (key) => (sort.key === key ? (sort.dir === "asc" ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />) : null);

  const load = useCallback(() => {
    const params = {};
    if (fStato !== "all") params.stato = fStato;
    if (dataDa) params.data_da = dataDa;
    if (dataA) params.data_a = dataA;
    api.get("/riparazioni", { params }).then(({ data }) => setItems(data));
    api.get("/riparazioni/stats").then(({ data }) => setStats(data));
  }, [fStato, dataDa, dataA]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const getVal = (r, key) => ({
    numero: r.numero_pratica, cliente: r.cliente_nome, dispositivo: r.tipologia_dispositivo,
    marca: [r.marca, r.modello].filter(Boolean).join(" "), seriale: r.seriale, stato: r.stato,
    tecnico: r.tecnico_assegnato, ritiro: r.data_ritiro, giorni: r.giorni_da_ritiro,
  }[key]);
  const rows = useMemo(() => {
    let list = items || [];
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((r) => [r.numero_pratica, r.cliente_nome, r.tipologia_dispositivo, r.marca, r.modello, r.seriale, r.stato, r.tecnico_assegnato].some((v) => String(v || "").toLowerCase().includes(q)));
    if (sort.key) {
      const dir = sort.dir === "asc" ? 1 : -1;
      list = [...list].sort((a, b) => {
        const va = getVal(a, sort.key), vb = getVal(b, sort.key);
        if (sort.key === "giorni") return ((va ?? -Infinity) - (vb ?? -Infinity)) * dir;
        return String(va || "").localeCompare(String(vb || ""), "it", { numeric: true, sensitivity: "base" }) * dir;
      });
    }
    return list;
  }, [items, search, sort]);

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader
          title="Riparazioni"
          subtitle="Accettazione dispositivi, workflow e monitoraggio tempi"
          actions={
            <Button data-testid="new-riparazione-btn" onClick={() => nav("/riparazioni/nuova")} className="rounded-sm gap-2">
              <Plus size={16} weight="bold" /> Nuova riparazione
            </Button>
          }
        />

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3 mb-6" data-testid="rip-dashboard">
            <StatCard label="Ritirato" value={stats.by_stato?.["Ritirato"] || 0} tone="primary" />
            <StatCard label="In diagnosi" value={stats.by_stato?.["In diagnosi"] || 0} tone="violet" />
            <StatCard label="In lavorazione" value={stats.by_stato?.["In lavorazione"] || 0} tone="amber" />
            <StatCard label="In attesa cliente" value={stats.by_stato?.["In attesa cliente"] || 0} tone="orange" />
            <StatCard label="In attesa ricambio" value={stats.by_stato?.["In attesa ricambio"] || 0} tone="rose" />
            <StatCard label="In attesa lavorazione est." value={stats.by_stato?.["In attesa lavorazione est."] || 0} tone="cyan" />
            <StatCard label="Pronto per riconsegna" value={stats.by_stato?.["Pronto per riconsegna"] || 0} tone="teal" />
          </div>
        )}

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input data-testid="rip-filter-search" placeholder="Cerca (pratica, cliente, dispositivo, seriale, tecnico…)" value={search} onChange={(e) => setSearch(e.target.value)} className="w-80 pl-8 rounded-sm bg-white" />
          </div>
          <Select value={fStato} onValueChange={setFStato}>
            <SelectTrigger className="w-48 rounded-sm bg-white" data-testid="rip-filter-stato"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              {RIP_STATI.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" data-testid="rip-filter-datada" value={dataDa} onChange={(e) => setDataDa(e.target.value)} className="w-40 rounded-sm bg-white" title="Ritiro dal" />
          <Input type="date" data-testid="rip-filter-dataa" value={dataA} onChange={(e) => setDataA(e.target.value)} className="w-40 rounded-sm bg-white" title="Ritiro al" />
        </div>

        <Card className="rounded-sm border-border shadow-none">
          <div className="overflow-x-auto">
          <Table style={{ tableLayout: "fixed", width: totalW }} className="[&_td]:overflow-hidden [&_th]:overflow-hidden">
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: colW.numero }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("numero")} data-testid="rip-sort-numero"><span className="inline-flex items-center gap-1">N° pratica{sortIcon("numero")}</span><RH k="numero" /></TableHead>
                <TableHead style={{ width: colW.cliente }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("cliente")} data-testid="rip-sort-cliente"><span className="inline-flex items-center gap-1">Cliente{sortIcon("cliente")}</span><RH k="cliente" /></TableHead>
                <TableHead style={{ width: colW.dispositivo }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("dispositivo")} data-testid="rip-sort-dispositivo"><span className="inline-flex items-center gap-1">Dispositivo{sortIcon("dispositivo")}</span><RH k="dispositivo" /></TableHead>
                <TableHead style={{ width: colW.marca }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("marca")} data-testid="rip-sort-marca"><span className="inline-flex items-center gap-1">Marca/Modello{sortIcon("marca")}</span><RH k="marca" /></TableHead>
                <TableHead style={{ width: colW.seriale }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("seriale")} data-testid="rip-sort-seriale"><span className="inline-flex items-center gap-1">Seriale{sortIcon("seriale")}</span><RH k="seriale" /></TableHead>
                <TableHead style={{ width: colW.stato }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("stato")} data-testid="rip-sort-stato"><span className="inline-flex items-center gap-1">Stato{sortIcon("stato")}</span><RH k="stato" /></TableHead>
                <TableHead style={{ width: colW.tecnico }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("tecnico")} data-testid="rip-sort-tecnico"><span className="inline-flex items-center gap-1">Tecnico{sortIcon("tecnico")}</span><RH k="tecnico" /></TableHead>
                <TableHead style={{ width: colW.ritiro }} className="relative cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("ritiro")} data-testid="rip-sort-ritiro"><span className="inline-flex items-center gap-1">Data ritiro{sortIcon("ritiro")}</span><RH k="ritiro" /></TableHead>
                <TableHead style={{ width: colW.giorni }} className="relative text-right cursor-pointer select-none hover:text-primary" onClick={() => toggleSort("giorni")} data-testid="rip-sort-giorni"><span className="inline-flex items-center gap-1 justify-end w-full">Giorni aperta{sortIcon("giorni")}</span><RH k="giorni" /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items && rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-12">
                  <Package size={26} className="mx-auto mb-2 opacity-50" /> Nessuna riparazione.
                </TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid="riparazione-row" className="hover:bg-secondary cursor-pointer" onClick={() => nav(`/riparazioni/${r.id}`)}>
                  <TableCell className="font-mono text-xs font-medium">{r.numero_pratica}</TableCell>
                  <TableCell className="text-muted-foreground max-w-[180px] truncate">{r.cliente_nome || "—"}</TableCell>
                  <TableCell className="capitalize">{r.tipologia_dispositivo || "—"}</TableCell>
                  <TableCell className="font-medium max-w-[180px] truncate">{[r.marca, r.modello].filter(Boolean).join(" ") || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{r.seriale || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={`rounded-sm ${RIP_STATO_COLOR[r.stato] || ""}`}>{r.stato}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{r.tecnico_assegnato || "—"}</TableCell>
                  <TableCell className="tabular-nums whitespace-nowrap">{fmtDate(r.data_ritiro)}</TableCell>
                  <TableCell className="text-right">
                    {r.giorni_da_ritiro != null && (
                      <span className={`inline-flex items-center gap-1 text-sm tabular-nums ${r.badge_ritardo ? "text-rose-600 font-semibold" : "text-muted-foreground"}`}>
                        {r.badge_ritardo ? <WarningCircle size={14} weight="fill" /> : <Clock size={14} />}
                        {r.giorni_da_ritiro}g
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </Card>
      </div>
    </InternalLayout>
  );
}
